import React, { ReactNode } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { StatusInfoLoading } from '../../components/StatusInfo/StatusInfoLoading';
import { StatusInfoPositiveCheck } from '../../components/StatusInfo/StatusInfoPositiveCheck';
import { StatusInfoErrorWithRequestId } from '../../components/StatusInfo';
import LiveRegion from '../../../components/atoms/LiveRegion/LiveRegion';
import { getRequestIdFromHeaders } from '../../../lib/HeaderUtils/RequestID';
import { SonResponseError, SonResponseErrorTypes } from '../types/SonResponseError';
import { FetchState } from '../../../hooks/reducers/useFetchReducer';
import { getDefaultErrorTypeTranslations } from '../utils/defaultErrorTypeTranslations';
import { translatePartOfStringWithLink } from '../../../lib/StringUtils/StringHTMLUtils';

export type DefaultStatusMessages = {
  messageSuccess: ReactNode;
  messageForward?: string | undefined;
  messageSending: ReactNode;
  messageFail: ReactNode;
};

/**
 * Custom Error Type Message for overriding errorTypes for specific forms.
 */
export type CustomErrorTypeMessages = {
  customErrorTypeMessages?: Partial<Record<SonResponseErrorTypes, string>>;
};

export type StatusMessages<T> = DefaultStatusMessages &
  CustomErrorTypeMessages & {
    customErrorResponseMapper?: (errorResponse: AxiosResponse<any>) => ReactNode | undefined;
    fetchState: FetchState<AxiosResponse<T>, AxiosError<SonResponseError>>;
  };

export function StatusMessages<T extends { url?: string }>({
  messageSuccess,
  messageForward,
  messageSending,
  messageFail,
  customErrorTypeMessages,
  fetchState,
  customErrorResponseMapper,
}: StatusMessages<T>) {
  const { t } = useTranslation();

  /**
   * Get custom messages for specific error types.
   */
  const getCustomDisplayableErrorMessage = (type: SonResponseErrorTypes): string | undefined => {
    return customErrorTypeMessages ? customErrorTypeMessages[type] : undefined;
  };

  const getFailMessage = (err: AxiosError<SonResponseError>): ReactNode => {
    const errorType = err?.response?.data?.errorType;

    if (errorType) {
      return getCustomDisplayableErrorMessage(errorType) || getDefaultErrorTypeTranslations(t, err?.response?.data);
    }

    return messageFail;
  };

  // for easier testing we provide every status with a data-form-status attribute
  const liveRegionMessage = () => {
    switch (fetchState.type) {
      case 'loading':
        return (
          <StatusInfoLoading modifier={['padding-top', 'right']} message={messageSending} dataFormStatus="sending" />
        );
      case 'success':
        return (
          <>
            <StatusInfoPositiveCheck
              modifier={['padding-top', 'right']}
              message={messageSuccess}
              dataFormStatus="success"
            />
            {messageForward && fetchState.response?.data?.url
              ? (
                  <StatusInfoLoading
                    modifier={['padding-top', 'right']}
                    message={translatePartOfStringWithLink(messageForward, fetchState.response?.data?.url)}
                    dataFormStatus="forwarding"
                  />
                )
              : null}
          </>
        );
      case 'error': {
        return (
          <StatusInfoErrorWithRequestId
            modifier={['padding-top', 'red', 'right']}
            role="alert"
            message={getFailMessage(fetchState.requestError)}
            additionalContent={customErrorResponseMapper?.(fetchState.requestError?.response as any)}
            requestId={getRequestIdFromHeaders(fetchState.requestError?.response?.headers)}
            dataFormStatus="fail"
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <LiveRegion htmlTag="div" invisible={false} ariaAtomic>
      {liveRegionMessage()}
    </LiveRegion>
  );
}
